import { useState } from 'react';
import { Trash2, Sparkles, AlertTriangle, HelpCircle, CheckCircle2, Loader2, Pencil, ExternalLink, ShieldAlert, ShieldCheck, FileText, MessageSquare, Link2, Unlink, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { ratingError } from '@/lib/owaspRating';
import RiskRatingField from '@/components/tabs/threats/RiskRatingField';
import SeverityBadge from '@/components/tabs/threats/SeverityBadge';
import type { QuestionOut, VulnerabilityOut, QuickScanFinding } from '@/services/questionsService';
import type { VulnerabilityFinding } from '@/types/ai';

export interface QuestionRowProps {
  question: QuestionOut;
  quickScanResult?: QuickScanFinding;
  isItemAnalyzing: boolean;
  canAnalyze: boolean;
  onDelete: (id: number) => void;
  onAnalyze: (question: QuestionOut) => void;
  onMarkStatus: (id: number, status: 'ok' | 'clarification' | 'vulnerable' | null) => void;
  onUpdateFinding: (questionId: number, vulnId: number, updated: Partial<VulnerabilityFinding>) => void;
  onUpdateQuestion: (id: number, question: string) => void;
  onUpdateAnswer: (id: number, answer: string) => void;
  onUpdateNotes: (id: number, notes: string) => void;
  onSendToThreatCat: (finding: VulnerabilityFinding) => void;
  isSending: boolean;
  sendResult: 'success' | 'error' | null;
  sendError?: string;
  sendDisabledReason?: string | null;
  projectVulnerabilities?: VulnerabilityOut[];
  onLinkVulnerability?: (questionId: number, vulnerabilityId: number) => void;
  onUnlinkVulnerability?: (questionId: number, vulnerabilityId: number) => void;
}

/** Convert persisted VulnerabilityOut to the VulnerabilityFinding shape used by ThreatCat push. */
function toVulnFinding(v: VulnerabilityOut, questionId: number, answer: string): VulnerabilityFinding {
  return {
    question_id: questionId,
    vulnerability_id: v.id,
    answer,
    is_vulnerable: true,
    needs_clarification: false,
    vulnerability_title: v.name,
    vulnerability_description: v.vulnerability,
    threat_scenario: v.scenario,
    countermeasure: v.countermeasure,
    reason: v.reason,
    rating_vector_string: v.rating_vector_string,
    asrg_ids: v.asrg_ids ? v.asrg_ids.split(',').map((s) => s.trim()).filter(Boolean) : [],
    is_full_analysis: true,
  };
}

export default function QuestionRow({
  question: q,
  quickScanResult,
  isItemAnalyzing,
  canAnalyze,
  onDelete,
  onAnalyze,
  onMarkStatus,
  onUpdateFinding,
  onUpdateQuestion,
  onUpdateAnswer,
  onUpdateNotes,
  onSendToThreatCat,
  isSending,
  sendResult,
  sendError,
  sendDisabledReason = null,
  projectVulnerabilities = [],
  onLinkVulnerability,
  onUnlinkVulnerability,
}: QuestionRowProps) {
  // Derive status from question props — support N vulnerabilities
  const persistedVulns = q.vulnerabilities ?? [];
  const hasPersistedVulns = persistedVulns.length > 0;
  const quickScanFinding: VulnerabilityFinding | undefined = !hasPersistedVulns && quickScanResult
    ? {
        question_id: q.id,
        answer: q.answer ?? '',
        is_vulnerable: quickScanResult.is_vulnerable,
        needs_clarification: quickScanResult.needs_clarification,
        vulnerability_title: quickScanResult.vulnerability_title,
        reason: quickScanResult.reason,
        rating_vector_string: quickScanResult.rating_vector_string,
        asrg_ids: quickScanResult.asrg_ids ?? [],
        is_full_analysis: false,
      }
    : undefined;
  const hasFinding = hasPersistedVulns || !!quickScanFinding;
  const manualStatus = (q.manual_status === 'vulnerable' || q.manual_status === 'quick_scan_vulnerable') ? 'vulnerable' as const
    : (q.manual_status === 'ok' || q.manual_status === 'quick_scan_ok') ? 'ok' as const
    : (q.manual_status === 'clarification' || q.manual_status === 'incorrect' || q.manual_status === 'quick_scan_clarification') ? 'clarification' as const
    : undefined;
  const isVulnerable = manualStatus === 'vulnerable';
  const asrgIdsList = q.asrg_ids ? q.asrg_ids.split(',').map((s) => s.trim()).filter(Boolean) : [];
  const [expandedVulnIds, setExpandedVulnIds] = useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingVulnId, setEditingVulnId] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftScenario, setDraftScenario] = useState('');
  const [draftCountermeasure, setDraftCountermeasure] = useState('');
  const [draftRating, setDraftRating] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(false);
  const [draftQuestion, setDraftQuestion] = useState('');
  const [editingAnswer, setEditingAnswer] = useState(false);
  const [draftAnswer, setDraftAnswer] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [draftNotes, setDraftNotes] = useState('');
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [userContextDraft, setUserContextDraft] = useState('');
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);

  // Vulnerabilities available to link (exclude already linked + resolved)
  const linkedVulnIds = new Set(persistedVulns.map((v) => v.id));
  const excludedStatuses = new Set(['mitigated', 'removed_from_tc']);
  const availableVulns = projectVulnerabilities.filter(
    (v) => !linkedVulnIds.has(v.id) && !excludedStatuses.has(v.status),
  );

  const handleStartEdit = (vuln: VulnerabilityOut) => {
    setDraftTitle(vuln.name ?? '');
    setDraftDescription(vuln.vulnerability ?? '');
    setDraftScenario(vuln.scenario ?? '');
    setDraftCountermeasure(vuln.countermeasure ?? '');
    setDraftRating(vuln.rating_vector_string ?? '');
    setEditingVulnId(vuln.id);
  };

  const handleSaveEdit = () => {
    if (editingVulnId === null) return;
    onUpdateFinding(q.id, editingVulnId, {
      vulnerability_title: draftTitle,
      vulnerability_description: draftDescription,
      threat_scenario: draftScenario,
      countermeasure: draftCountermeasure,
      rating_vector_string: draftRating.trim() || null,
    });
    setEditingVulnId(null);
  };

  const handleCancelEdit = () => setEditingVulnId(null);

  const toggleVulnExpanded = (vulnId: number) => {
    setExpandedVulnIds((prev) => {
      const next = new Set(prev);
      if (next.has(vulnId)) { next.delete(vulnId); if (editingVulnId === vulnId) setEditingVulnId(null); }
      else next.add(vulnId);
      return next;
    });
  };

  const handleSaveQuestion = () => {
    if (draftQuestion.trim()) onUpdateQuestion(q.id, draftQuestion.trim());
    setEditingQuestion(false);
  };

  const handleSaveAnswer = () => {
    onUpdateAnswer(q.id, draftAnswer);
    setEditingAnswer(false);
  };

  const handleSaveNotes = () => {
    onUpdateNotes(q.id, draftNotes);
    setEditingNotes(false);
  };

  const handleAnalyzeWithContext = () => {
    setContextDialogOpen(false);
    // Pass the question with updated user_context to the parent, which persists it before analyzing
    onAnalyze({ ...q, user_context: userContextDraft.trim() || q.user_context });
  };

  const statusStyle = manualStatus === 'ok'
    ? 'border-l-green-500 bg-green-50/50 dark:bg-green-950/10'
    : manualStatus === 'clarification'
    ? 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20'
    : hasPersistedVulns || quickScanFinding?.is_vulnerable
    ? 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20'
    : quickScanFinding?.needs_clarification
    ? 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20'
    : quickScanFinding
    ? 'border-l-green-500 bg-green-50/50 dark:bg-green-950/10'
    : 'border-l-transparent';

  return (
    <div className={`py-3 rounded-r px-3 -mx-2 border-l-4 transition-colors duration-150 ${statusStyle}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 flex flex-col items-end gap-0.5">
          <span className="text-sm font-mono text-muted-foreground">{q.question_number}</span>
          {q.external_id && (
            <span className="font-mono text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 leading-tight whitespace-nowrap">{q.external_id}</span>
          )}
        </div>
        <div className="flex-1 space-y-1">
          {editingQuestion ? (
            <div className="space-y-1.5">
              <Textarea
                value={draftQuestion}
                onChange={(e) => setDraftQuestion(e.target.value)}
                rows={2}
                className="text-sm"
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveQuestion} disabled={!draftQuestion.trim()}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingQuestion(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm">{q.question}</p>
          )}
          {asrgIdsList.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {asrgIdsList.map((aid) => (
                <span
                  key={aid}
                  className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                >
                  {aid}
                </span>
              ))}
            </div>
          )}

          {/* Answer & finding */}
          {editingAnswer ? (
            <div className="mt-2 space-y-1.5">
              <Textarea
                placeholder="Enter your answer…"
                value={draftAnswer}
                onChange={(e) => setDraftAnswer(e.target.value)}
                rows={2}
                className="text-sm"
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveAnswer}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingAnswer(false)}>Cancel</Button>
              </div>
            </div>
          ) : q.answer ? (
            <div className="mt-2 space-y-1.5">
              <div className="flex items-start gap-1">
                <div className="text-sm text-muted-foreground border-l-2 border-muted pl-2 flex-1">
                  <span className="font-medium">Answer: </span>
                  <span>{q.answer}</span>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => { setDraftAnswer(q.answer ?? ''); setEditingAnswer(true); }}
                      className="rounded p-1 text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted shrink-0 mt-0.5"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Edit answer</TooltipContent>
                </Tooltip>
              </div>
              {isItemAnalyzing && (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Analyzing…
                </span>
              )}
            </div>
          ) : (
            <div className="mt-2">
              <Input
                placeholder="Type your answer…"
                className="text-sm h-8 bg-muted/30 border-dashed cursor-text"
                onFocus={() => { setDraftAnswer(''); setEditingAnswer(true); }}
                readOnly
              />
            </div>
          )}

          {/* Notes */}
          <div className="mt-1.5">
            {editingNotes ? (
              <div className="space-y-1.5">
                <Textarea
                  placeholder="Add notes…"
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  rows={2}
                  className="text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveNotes}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingNotes(false)}>Cancel</Button>
                </div>
              </div>
            ) : q.notes ? (
              <div className="flex items-start gap-1">
                <div className="text-xs text-muted-foreground border-l-2 border-muted/60 pl-2 flex-1 italic">
                  <MessageSquare className="inline h-3 w-3 mr-1 -mt-0.5" />
                  {q.notes}
                </div>
                <button
                  onClick={() => { setDraftNotes(q.notes ?? ''); setEditingNotes(true); }}
                  className="rounded p-0.5 text-muted-foreground/30 hover:text-muted-foreground hover:bg-muted shrink-0"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setDraftNotes(''); setEditingNotes(true); }}
                className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              >
                <MessageSquare className="inline h-3 w-3 mr-1 -mt-0.5" />
                Add notes…
              </button>
            )}
          </div>

          {/* User context (if set) */}
          {q.user_context && (
            <div className="mt-1 text-xs text-muted-foreground/70 border-l-2 border-blue-200 dark:border-blue-800 pl-2">
              <span className="font-medium">Context:</span> {q.user_context}
            </div>
          )}

          {/* ── Status badges ──────────────────────────────────────── */}
          {!hasFinding && manualStatus && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {manualStatus === 'ok' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  <CheckCircle2 className="h-3 w-3" />
                  {q.manual_status === 'quick_scan_ok' ? 'OK (quick scan)' : 'OK (manual)'}
                </span>
              )}
              {manualStatus === 'clarification' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  <HelpCircle className="h-3 w-3" />
                  {q.manual_status === 'quick_scan_clarification' ? 'Needs clarification (quick scan)' : 'Needs clarification (manual)'}
                </span>
              )}
              {manualStatus === 'vulnerable' && !hasPersistedVulns && (
                <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
                  <ShieldAlert className="h-3 w-3" />
                  {q.manual_status === 'quick_scan_vulnerable' ? 'Vulnerable (quick scan)' : 'Vulnerable'}
                </span>
              )}
            </div>
          )}

          {/* ── Link vulnerability combobox ── */}
          {(isVulnerable || hasPersistedVulns) && onLinkVulnerability && (
            <div className="mt-2">
              <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    <Link2 className="h-3 w-3" />
                    Link to vulnerability
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[320px]" align="start">
                  <Command>
                    <CommandInput placeholder="Search vulnerabilities…" />
                    <CommandList>
                      <CommandEmpty>No vulnerabilities yet — run full analysis or create one in Threats tab.</CommandEmpty>
                      <CommandGroup>
                        {availableVulns.map((v) => (
                          <CommandItem
                            key={v.id}
                            value={`${v.name} ${v.external_id ?? ''}`}
                            onSelect={() => {
                              onLinkVulnerability(q.id, v.id);
                              setLinkPopoverOpen(false);
                            }}
                            className="flex flex-col items-start gap-0.5"
                          >
                            <span className="text-sm font-medium truncate w-full">{v.name}</span>
                            <span className="text-[10px] text-muted-foreground">
                              {v.source}{v.external_id ? ` · ${v.external_id}` : ''}{v.rating_vector_string ? ` · ${v.rating_vector_string.slice(0, 20)}…` : ''}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* ── Quick scan result (no persisted vulns) ──────────── */}
          {quickScanFinding && (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {!manualStatus && quickScanFinding.is_vulnerable && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
                    <AlertTriangle className="h-3 w-3" />
                    Vulnerability
                  </span>
                )}
                {!manualStatus && !quickScanFinding.is_vulnerable && quickScanFinding.needs_clarification && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <HelpCircle className="h-3 w-3" />
                    Needs clarification
                  </span>
                )}
                {!manualStatus && !quickScanFinding.is_vulnerable && !quickScanFinding.needs_clarification && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    <CheckCircle2 className="h-3 w-3" />
                    OK
                  </span>
                )}
                {manualStatus === 'ok' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    <CheckCircle2 className="h-3 w-3" />
                    {q.manual_status === 'quick_scan_ok' ? 'OK (quick scan)' : 'OK (manual)'}
                  </span>
                )}
                {manualStatus === 'clarification' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <HelpCircle className="h-3 w-3" />
                    {q.manual_status === 'quick_scan_clarification' ? 'Needs clarification (quick scan)' : 'Needs clarification (manual)'}
                  </span>
                )}
                <SeverityBadge vector={quickScanFinding.rating_vector_string} />
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  Quick scan
                </span>
              </div>
              {!manualStatus && (quickScanFinding.is_vulnerable || quickScanFinding.needs_clarification) && quickScanFinding.reason && (
                <p className="text-xs text-muted-foreground italic">
                  {quickScanFinding.reason}
                </p>
              )}
            </div>
          )}

          {/* ── Persisted vulnerabilities (N cards) ────────────── */}
          {hasPersistedVulns && (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {!manualStatus && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
                    <AlertTriangle className="h-3 w-3" />
                    {persistedVulns.length === 1 ? 'Vulnerability' : `${persistedVulns.length} Vulnerabilities`}
                  </span>
                )}
                {manualStatus === 'ok' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    <CheckCircle2 className="h-3 w-3" />
                    OK (manual)
                  </span>
                )}
                {manualStatus === 'clarification' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <HelpCircle className="h-3 w-3" />
                    Needs clarification (manual)
                  </span>
                )}
              </div>
              {isItemAnalyzing && (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Analyzing…
                </span>
              )}
              <div className="space-y-1.5">
                {persistedVulns.map((vuln) => {
                  const isExpanded = expandedVulnIds.has(vuln.id);
                  const isEditing = editingVulnId === vuln.id;
                  const vulnAsrgIds = vuln.asrg_ids ? vuln.asrg_ids.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                  return (
                    <div key={vuln.id} className="rounded-lg border bg-card text-sm overflow-hidden animate-in fade-in-0 duration-200">
                      {/* Compact header — always visible */}
                      <button
                        onClick={() => toggleVulnExpanded(vuln.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/40 transition-colors text-left"
                      >
                        {vuln.external_id && (
                          <span className="font-mono text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0">{vuln.external_id}</span>
                        )}
                        <span className="text-sm font-medium truncate flex-1">{vuln.name}</span>
                        <SeverityBadge vector={vuln.rating_vector_string} threatType={vuln.threat_type} />
                        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Expanded details */}
                      {isExpanded && (
                        isEditing ? (
                          <div className="p-4 space-y-3 border-t">
                            <div className="space-y-1">
                              <label className="text-xs font-medium text-muted-foreground">Title</label>
                              <Input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium text-muted-foreground">Description</label>
                              <Textarea value={draftDescription} onChange={(e) => setDraftDescription(e.target.value)} rows={3} />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium text-muted-foreground">Threat scenario</label>
                              <Textarea value={draftScenario} onChange={(e) => setDraftScenario(e.target.value)} rows={3} />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium text-muted-foreground">Countermeasure</label>
                              <Textarea value={draftCountermeasure} onChange={(e) => setDraftCountermeasure(e.target.value)} rows={3} />
                            </div>
                            <RiskRatingField value={draftRating} onChange={setDraftRating} threatType={vuln.threat_type} />
                            <div className="flex gap-2 pt-1">
                              <Button size="sm" onClick={handleSaveEdit} disabled={!!ratingError(draftRating)}>Save</Button>
                              <Button size="sm" variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* ── Sections ──────────────────────────────── */}
                            <div className="divide-y border-t">
                              {vuln.vulnerability && (
                                <div className="px-4 py-3 flex gap-3">
                                  <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Description</p>
                                    <p className="text-sm leading-relaxed">{vuln.vulnerability}</p>
                                  </div>
                                </div>
                              )}
                              {vuln.scenario && (
                                <div className="px-4 py-3 flex gap-3">
                                  <ShieldAlert className="h-4 w-4 text-destructive/70 shrink-0 mt-0.5" />
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Threat Scenario</p>
                                    <p className="text-sm leading-relaxed">{vuln.scenario}</p>
                                  </div>
                                </div>
                              )}
                              {vuln.countermeasure && (
                                <div className="px-4 py-3 flex gap-3">
                                  <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-500 shrink-0 mt-0.5" />
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-muted-foreground mb-0.5">Countermeasure</p>
                                    <p className="text-sm leading-relaxed">{vuln.countermeasure}</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* ── Footer: risk rating + ASRG + actions ─────────── */}
                            <div className="px-4 py-2.5 border-t bg-muted/30 flex flex-wrap items-center gap-2">
                              <SeverityBadge vector={vuln.rating_vector_string} threatType={vuln.threat_type} />
                              {ratingError(vuln.rating_vector_string) && (
                                <span className="text-xs text-amber-600 dark:text-amber-400">
                                  Invalid risk rating — edit before sending
                                </span>
                              )}
                              {vulnAsrgIds.length > 0 && vulnAsrgIds.map((aid: string) => (
                                <span
                                  key={aid}
                                  className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                                >
                                  {aid}
                                </span>
                              ))}
                              <div className="ml-auto flex items-center gap-1.5">
                                {onUnlinkVulnerability && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-xs gap-1 text-muted-foreground hover:text-destructive"
                                        onClick={() => onUnlinkVulnerability(q.id, vuln.id)}
                                      >
                                        <Unlink className="h-3.5 w-3.5" />
                                        Unlink
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Remove link between this question and the vulnerability</TooltipContent>
                                  </Tooltip>
                                )}
                                {(() => {
                                  const isSyncedFindingLocked = Boolean(vuln.threatcat_threat_id && sendDisabledReason);
                                  const editFindingTitle = isSyncedFindingLocked
                                    ? (sendDisabledReason ?? 'Edit finding')
                                    : 'Edit finding';
                                  const sendTooltip = vuln.threatcat_threat_id
                                    ? 'Already pushed to ThreatCat'
                                    : (sendDisabledReason ?? 'Push vulnerability to ThreatCat');
                                  const isSendDisabled = isSending || !!vuln.threatcat_threat_id || Boolean(sendDisabledReason);

                                  return (
                                    <>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span title={isSyncedFindingLocked ? (sendDisabledReason ?? undefined) : undefined} className="inline-flex">
                                            <button
                                              type="button"
                                              aria-label="Edit finding"
                                              title={editFindingTitle}
                                              disabled={isSyncedFindingLocked}
                                              onClick={() => {
                                                if (isSyncedFindingLocked) return;
                                                handleStartEdit(vuln);
                                              }}
                                              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{editFindingTitle}</TooltipContent>
                                      </Tooltip>
                                      {(vuln.threatcat_threat_id || sendResult === 'success') && (
                                        <span className="text-xs text-green-600 dark:text-green-400">Sent</span>
                                      )}
                                      {sendResult === 'error' && sendError && (
                                        <span className="text-xs text-destructive">{sendError}</span>
                                      )}
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span title={sendDisabledReason ?? undefined} className="inline-flex">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs gap-1.5"
                                              title={sendTooltip}
                                              onClick={() => {
                                                if (sendDisabledReason) return;
                                                onSendToThreatCat(toVulnFinding(vuln, q.id, q.answer ?? ''));
                                              }}
                                              disabled={isSendDisabled}
                                            >
                                              {isSending
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : <ExternalLink className="h-3.5 w-3.5" />
                                              }
                                              ThreatCat
                                            </Button>
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          {sendTooltip}
                                        </TooltipContent>
                                      </Tooltip>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {canAnalyze && isVulnerable && !hasPersistedVulns && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={`Full analysis for question ${q.id}`}
                  onClick={() => { setUserContextDraft(q.user_context ?? ''); setContextDialogOpen(true); }}
                  disabled={isItemAnalyzing}
                  className="rounded p-1.5 text-muted-foreground hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950/30 disabled:opacity-40"
                >
                  {isItemAnalyzing
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Sparkles className="h-4 w-4" />
                  }
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Full analysis</TooltipContent>
            </Tooltip>
          )}
          {q.answer?.trim() && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label="Mark as vulnerable"
                    onClick={() => onMarkStatus(q.id, isVulnerable ? null : 'vulnerable')}
                    className={`rounded p-1.5 ${
                      isVulnerable
                        ? 'text-destructive'
                        : 'text-muted-foreground hover:text-destructive'
                    } hover:bg-destructive/10`}
                  >
                    <ShieldAlert className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {isVulnerable ? 'Remove vulnerable mark' : 'Mark as vulnerable'}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label="Needs clarification"
                    onClick={() => onMarkStatus(q.id, manualStatus === 'clarification' ? null : 'clarification')}
                    className={`rounded p-1.5 ${
                      manualStatus === 'clarification'
                        ? 'text-amber-500'
                        : 'text-muted-foreground hover:text-amber-500'
                    } hover:bg-amber-50 dark:hover:bg-amber-950/30`}
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {manualStatus === 'clarification' ? 'Remove clarification mark' : 'Needs clarification'}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label="Mark as OK"
                    onClick={() => onMarkStatus(q.id, manualStatus === 'ok' ? null : 'ok')}
                    className={`rounded p-1.5 ${
                      manualStatus === 'ok'
                        ? 'text-green-500'
                        : 'text-muted-foreground hover:text-green-500'
                    } hover:bg-green-50 dark:hover:bg-green-950/30`}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {manualStatus === 'ok' ? 'Remove OK mark' : 'Mark as OK'}
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {!editingQuestion && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => { setDraftQuestion(q.question); setEditingQuestion(true); }}
                  className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Edit question</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={`Delete question ${q.id}`}
                onClick={() => setConfirmOpen(true)}
                className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Delete question</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete question</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete question <strong>#{q.id}</strong>? This will also remove any analysis results for it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => { setConfirmOpen(false); onDelete(q.id); }}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Analyze context dialog ──────────────────────────────────── */}
      <Dialog open={contextDialogOpen} onOpenChange={setContextDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Run Full Analysis</DialogTitle>
            <DialogDescription>
              Optionally provide additional context about this question before running AI analysis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs font-medium text-muted-foreground">Additional context (optional)</label>
            <Textarea
              placeholder="e.g. This component handles sensitive PII data and is exposed via CAN bus…"
              value={userContextDraft}
              onChange={(e) => setUserContextDraft(e.target.value)}
              rows={3}
              className="text-sm"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setContextDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAnalyzeWithContext}>
              <Sparkles className="mr-2 h-4 w-4" />
              Analyze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
